import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  signal,
  ViewChildren,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, distinctUntilChanged, Observable, of } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { isPageRootId } from '@blaze-runtime/engine-domain';
import { getBackground, StickySlots } from '@blaze-shared/elements-domain';
import { countedShareReplay, isDeepEqual, resizeRxUtils } from '@blaze-shared/shared-util';
import { CssMediaService } from '@blaze-shared/theming-data-access';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { NavElementPropsService } from '../../services/nav-element-props.service';
import { ViewportService } from '../../services/viewport.service';
import { CanvasElementBaseComponent } from '../__base/base.component';

import { BottombarSlotProps, HeaderSlotProps, SidebarSlotProps } from './root.models';

@UntilDestroy()
@Component({
  selector: 'be-root',
  templateUrl: './root.component.html',
  styleUrls: ['./root.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.sidebar-full-h]': 'isFullHeightSidebar()',
    '[style.--r-t-container-bg]': 'containerBg()',
    '[style.--r-t-container-padding]': 'containerPadding()',
  },
  standalone: false,
})
export class RootComponent
  extends CanvasElementBaseComponent<'root'>
  implements CanvasElementComponent<'root'>, OnInit, AfterViewInit, OnDestroy
{
  private readonly navElementPropsService = inject(NavElementPropsService);
  private readonly viewportService = inject(ViewportService);
  private readonly cssMediaService = inject(CssMediaService);
  private readonly document = inject<Document>(DOCUMENT);

  @ViewChildren('header') headerList!: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren('side') sideList!: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren('bottom') bottomList!: QueryList<ElementRef<HTMLElement>>;

  private readonly _printEffect = effect(() => {
    const isPrinting = this.cssMediaService.isPrintMode();
    const bg =
      this.containerBg() ||
      getComputedStyle(this.document.body).getPropertyValue('--bat-canvas').trim();

    if (isPrinting && bg) {
      this.applyPrintBackground(bg);
    } else {
      this.clearPrintBackground();
    }
  });

  headerSlotProps$!: Observable<HeaderSlotProps>;
  sidebarSlotProps$!: Observable<SidebarSlotProps>;
  bottombarSlotProps$!: Observable<BottombarSlotProps>;

  containerBg = signal<string | undefined>(undefined);
  containerPadding = signal<string | undefined>(undefined);
  headerHeight = signal(0);
  sidebarHeight = signal(0);
  bottombarHeight = signal(0);
  isFullHeightSidebar = signal(false);
  isStickyHeader = signal(false);
  isStickySidebar = signal(false);
  isStickyBottombar = signal(false);

  stickySlots = computed<StickySlots>(() => ({
    header: this.isStickyHeader(),
    side: this.isStickySidebar(),
    bottom: this.isStickyBottombar(),
  }));

  constructor() {
    super();

    this.initStyleHandler();
  }

  ngOnInit(): void {
    this.headerSlotProps$ = this.getHeaderSlotProps();
    this.sidebarSlotProps$ = this.getSidebarSlotProps();
    this.bottombarSlotProps$ = this.getBottombarSlotProps();

    this.headerSlotProps$.pipe(untilDestroyed(this)).subscribe(({ isSticky }) => {
      this.isStickyHeader.set(isSticky);
    });

    this.sidebarSlotProps$.pipe(untilDestroyed(this)).subscribe(({ isSticky, isFullHeight }) => {
      this.isStickySidebar.set(isSticky);
      this.isFullHeightSidebar.set(isFullHeight);
    });

    this.bottombarSlotProps$.pipe(untilDestroyed(this)).subscribe(({ isSticky }) => {
      this.isStickyBottombar.set(isSticky);
    });
  }

  ngAfterViewInit(): void {
    const headerHeight$ = this.trackElementListHeight(this.headerList);
    const sidebarHeight$ = this.trackElementListHeight(this.sideList);
    const bottombarHeight$ = this.trackElementListHeight(this.bottomList);

    headerHeight$.pipe(untilDestroyed(this)).subscribe((height) => {
      this.viewportService.updateNavHeaderHeight(height);
      this.headerHeight.set(height);
    });
    sidebarHeight$.pipe(untilDestroyed(this)).subscribe((height) => {
      this.sidebarHeight.set(height);
    });
    bottombarHeight$.pipe(untilDestroyed(this)).subscribe((height) => {
      this.viewportService.updateNavBottombarHeight(height);
      this.bottombarHeight.set(height);
    });
  }

  ngOnDestroy(): void {
    this.clearPrintBackground();
  }

  private getHeaderSlotProps(): Observable<HeaderSlotProps> {
    return this.navElementPropsService
      .observeContainerPropsFromRootElement(this.element$, 'navHeaderContainer')
      .pipe(
        map((props) => ({
          isSticky: this.isInEditor ? false : !!props?.content.isSticky,
        })),
        distinctUntilChanged(isDeepEqual),
        countedShareReplay(),
      );
  }

  private getSidebarSlotProps(): Observable<SidebarSlotProps> {
    return this.navElementPropsService
      .observeContainerPropsFromRootElement(this.element$, `navSidebarContainer`)
      .pipe(
        map((props) => ({
          isSticky: this.isInEditor ? false : !!props?.content.isSticky,
          isFullHeight: !!props?.content.isFullHeight,
        })),
        distinctUntilChanged(isDeepEqual),
        countedShareReplay(),
      );
  }

  private getBottombarSlotProps(): Observable<BottombarSlotProps> {
    return this.navElementPropsService
      .observeContainerPropsFromRootElement(this.element$, 'navBottombarContainer')
      .pipe(
        map((props) => ({
          isSticky: this.isInEditor ? false : !!props?.content.isSticky,
        })),
        distinctUntilChanged(isDeepEqual),
        countedShareReplay(),
      );
  }

  private trackElementListHeight(list: QueryList<ElementRef<HTMLElement>>): Observable<number> {
    return list.changes.pipe(
      map((changes) => changes.first?.nativeElement),
      startWith(list.first?.nativeElement),
      switchMap((htmlElement) => {
        if (!htmlElement) {
          return of(0);
        }

        return resizeRxUtils
          .fromResizeObserver(htmlElement)
          .pipe(map((entries) => entries.at(-1)?.contentRect.height ?? 0));
      }),
      countedShareReplay(),
    );
  }

  private applyPrintBackground(bg: string): void {
    this.document.documentElement.style.setProperty('background-color', bg, 'important');
    this.document.documentElement.style.setProperty('print-color-adjust', 'exact', 'important');
  }

  private clearPrintBackground(): void {
    this.document.documentElement.style.removeProperty('background-color');
    this.document.documentElement.style.removeProperty('print-color-adjust');
  }

  /**
   * Only page roots receive style props; roots of partial views (header, sidebar, bottombar)
   * do not.
   * @private
   */
  private initStyleHandler(): void {
    const isPageRoot$ = combineLatest([
      this.elementId$,
      this.store.select(runtimeSelectors.selectPageId),
    ]).pipe(
      map(([elementId, pageId]) => isPageRootId(elementId, pageId)),
      distinctUntilChanged(),
    );

    combineLatest({
      isPageRoot: isPageRoot$,
      props: this.props$.pipe(distinctUntilChanged(isDeepEqual)),
    })
      .pipe(untilDestroyed(this))
      .subscribe(({ isPageRoot, props }) => {
        if (!isPageRoot) {
          this.containerBg.set(undefined);
          this.containerPadding.set(undefined);
          return;
        }

        const { container } = props?.style || {};
        if (!container) {
          return;
        }

        this.containerBg.set(getBackground(container.fill));
        this.containerPadding.set(container.offset?.padding);
      });
  }
}
